import { useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ArrowForward from "@mui/icons-material/ArrowForward";

const SOLUTIONS = {
  strategicPlanning: {
    label: "We have lots of events, but no overall plan.",
    title: "Turn Separate Activities into an Intentional Evangelism Strategy",
    problemDescription:
      "As a church grows, evangelism can become a busy calendar of events, courses, outreach days and ministry initiatives. Each activity may be valuable, but leaders can struggle to see how they fit together, where each one is meant to lead and what should be prioritised next.",
    pathwaysFeatureDescription:
      "PATHWAYS helps you connect your evangelistic activities into intentional pathways. Plan campaigns, connect activities and follow-up, and give your team a shared picture of how different parts of your ministry work together.",
    cta: { label: "Build a Clearer Evangelism Strategy", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-ChurchPlanting.JPG",
      alt: "Evangelism strategy and campaign planning",
    },
  },

  measuringEffectiveness: {
    label: "We don't know which outreach methods are actually working.",
    title: "Learn from What Happens and Make Better Decisions",
    problemDescription:
      "Churches can invest significant time, money and volunteer energy into events, door knocking, street evangelism, courses and other outreach. Yet leaders are often left with individual stories and headline numbers rather than a clear picture of what each approach is producing.",
    pathwaysFeatureDescription:
      "PATHWAYS connects outreach activity with the conversations, engagement and follow-up that result from it. It gives leaders a fuller picture so they can notice patterns, ask better questions and make more informed decisions about future evangelism.",
    cta: { label: "Start Learning from Your Outreach", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-EvangelisticCourses.JPG",
      alt: "Measuring and improving evangelism effectiveness",
    },
  },

  peopleFallingThroughCracks: {
    label: "People keep falling through the cracks in our systems.",
    title: "Never Wonder: “Did Anyone Follow Them Up?”",
    problemDescription:
      "Someone comes to an event, has a meaningful conversation or says they would like to hear more. A volunteer intends to follow up, but the handover happens informally, responsibility is unclear or the information disappears among everything else happening in church life.",
    pathwaysFeatureDescription:
      "PATHWAYS gives your team a shared history of what has happened and what might need to happen next. Connect events, conversations and relationships in one place so follow-up becomes part of a reliable ministry process rather than something that depends on individual memory.",
    cta: { label: "Create More Reliable Follow-Up", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-FollowUp.JPG",
      alt: "Structured evangelism follow-up",
    },
  },

  scatteredNotes: {
    label: "Important notes are buried in WhatsApp chats and spreadsheets.",
    title: "Give Your Evangelism Team One Shared Memory",
    problemDescription:
      "Useful ministry knowledge often ends up wherever was convenient at the time: WhatsApp, spreadsheets, emails, notebooks or someone's memory. In a large team, no single leader can piece all of those fragments together well enough to see the complete history.",
    pathwaysFeatureDescription:
      "PATHWAYS gives your team one shared place to record meaningful information against the people, addresses, events and activities it relates to. Leaders can see the history without chasing different volunteers for context, and important knowledge remains with the ministry rather than the individual.",
    cta: { label: "Bring Your Team's Knowledge Together", href: "#signup" },
    image: {
      src: "/Hero/Hero-Left.JPG",
      alt: "Shared evangelism notes and team information",
    },
  },

  fieldNotes: {
    label:
      "We need a way to record notes from door knocking and street evangelism.",
    title: "Capture What Your Team Learns While They Are Out",
    problemDescription:
      "Some of the most valuable ministry insight comes from short conversations on streets and doorsteps. But asking volunteers to remember everything afterwards rarely works, while complicated reporting makes people less likely to record anything at all.",
    pathwaysFeatureDescription:
      "PATHWAYS gives volunteers a simple way to record useful information while doing outreach. Connect door-knocking conversations to addresses, street conversations to people and interactions to the wider ministry—giving leaders visibility without expecting every volunteer to become an administrator.",
    cta: { label: "Start Capturing Outreach Conversations", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-StreetEvangelism.JPG",
      alt: "Recording door knocking and street evangelism notes",
    },
  },

  interactionPatterns: {
    label: "We struggle to spot patterns across hundreds of interactions.",
    title: "See What No One Conversation Can Tell You",
    problemDescription:
      "When dozens of volunteers are having hundreds of conversations, no individual can remember enough to recognise everything the church is learning. Recurring questions, barriers, opportunities and patterns of engagement can remain hidden across separate interactions.",
    pathwaysFeatureDescription:
      "PATHWAYS brings those interactions into one structured picture. Leaders can look beyond isolated stories and begin to understand the recurring themes emerging across the ministry, helping the church listen more carefully to the community it is trying to reach.",
    cta: { label: "See the Bigger Picture", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-EvangelisticCourses.JPG",
      alt: "Identifying patterns across evangelism interactions",
    },
  },

  consistentFollowup: {
    label: "We struggle to consistently follow up with people.",
    title: "Turn Good Intentions into Consistent Follow-Up",
    problemDescription:
      "Churches regularly meet people who are open to another conversation, interested in an event or worth staying in touch with. But when follow-up depends on individual volunteers remembering promises and passing information on informally, consistency becomes difficult.",
    pathwaysFeatureDescription:
      "PATHWAYS gives your team a shared view of previous interactions, relationships and next steps. Whoever follows up can understand the context, avoid unnecessary repetition and continue the relationship warmly and intentionally.",
    cta: { label: "Build Consistent Follow-Up", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-FollowUp.JPG",
      alt: "Consistent follow-up for evangelism contacts",
    },
  },

  teamKnowledge: {
    label: "Useful knowledge disappears when people leave the team.",
    title: "Keep Ministry Knowledge with the Ministry",
    problemDescription:
      "A surprising amount of evangelism knowledge lives inside individual people's heads. When a volunteer steps back, a staff member changes role or an evangelism lead leaves the church, years of context about people, conversations and previous outreach can disappear with them.",
    pathwaysFeatureDescription:
      "PATHWAYS creates a shared ministry history. Relevant notes, interactions and relationship context remain available to the team, helping new leaders understand what has happened before and continue the ministry without starting again from memory.",
    cta: { label: "Build a Shared Ministry Memory", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-ChristianUnions.JPG",
      alt: "Preserving shared ministry knowledge",
    },
  },

  leadershipVisibility: {
    label:
      "Our leaders don’t have enough visibility to make good strategic decisions.",
    title: "Give Evangelism Leaders the Full Picture",
    problemDescription:
      "Leaders may hear encouraging stories, see event attendance and receive updates from individual ministry teams while still lacking a coherent view of evangelism as a whole. That makes it difficult to know where attention is needed, what should change and what opportunities deserve greater investment.",
    pathwaysFeatureDescription:
      "PATHWAYS brings outreach activity, people, interactions and follow-up into one shared picture. Instead of reconstructing the ministry from anecdotes and separate reports, evangelism leaders can see what is happening across teams and make decisions with greater context.",
    cta: { label: "Lead with Greater Visibility", href: "#signup" },
    image: {
      src: "/Solutions/Solutions-DoorKnocking.JPG",
      alt: "Strategic visibility for church evangelism leaders",
    },
  },
} as const;

const SolutionsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const solutionKeys = useMemo(() => Object.keys(SOLUTIONS), []);
  const activeKey = solutionKeys[activeTab] as keyof typeof SOLUTIONS;
  const active = SOLUTIONS[activeKey];

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg,#0f172a,#1e293b)",
        py: { xs: 6, md: 10 },
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          textAlign="center"
          sx={{
            color: "#fcfaf6",
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          Solve the Problems That Appear as Evangelism Grows
        </Typography>

        <Typography
          variant="h6"
          textAlign="center"
          sx={{
            color: "rgba(255,255,255,0.78)",
            maxWidth: 760,
            mx: "auto",
            mb: 6,
            lineHeight: 1.6,
          }}
        >
          PATHWAYS helps evangelism leads turn scattered activity into a ministry
          they can see, coordinate and learn from.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 4,
          }}
        >
          <Tabs
            orientation={isMobile ? "horizontal" : "vertical"}
            variant="scrollable"
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minWidth: isMobile ? "100%" : "260px",
              backgroundColor: "#1e293b",
              borderRadius: 3,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                color: "#fcfaf6",
                borderRadius: 2,
                p: 2,
                transition: "all 0.3s ease",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
                "&.Mui-selected": {
                  backgroundColor: "#4ade80",
                  color: "#0f172a",
                  boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                },
                "&:hover": {
                  backgroundColor: "rgba(74,222,128,0.1)",
                },
              },
            }}
          >
            {solutionKeys.map((key) => (
              <Tab
                key={key}
                label={SOLUTIONS[key as keyof typeof SOLUTIONS].label}
              />
            ))}
          </Tabs>

          <Box sx={{ flex: 1 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeKey}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.45 }}
              >
                <Box
                  sx={{
                    borderRadius: 5,
                    p: { xs: 3, md: 4 },
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      right: -80,
                      top: -80,
                      width: 220,
                      height: 220,
                      borderRadius: "50%",
                      background: "rgba(77, 222, 128, 0.35)",
                      filter: "blur(2px)",
                    }}
                  />

                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: "white", fontWeight: 900, mt: 1, mb: 3 }}
                    >
                      {active.title}
                    </Typography>

                    <Typography sx={{ color: "rgba(255,255,255,0.86)" }}>
                      {active.problemDescription}
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        mt: 2,
                      }}
                    >
                      {active.pathwaysFeatureDescription}
                    </Typography>
                  </Box>

                  <Box
                    component={motion.img}
                    src={active.image.src}
                    alt={active.image.alt}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    sx={{
                      width: "100%",
                      objectFit: "cover",
                      borderRadius: 3,
                      display: "block",
                      boxShadow: "none",
                      border: "none",
                    }}
                  />

                  <Box display="flex" justifyContent="center" pt={1}>
                    <Button
                      variant="contained"
                      sx={{
                        background: "#4ade80",
                        color: "#0f172a",
                        borderRadius: "9999px",
                        px: 4,
                        py: 1.5,
                        fontSize: "1rem",
                        boxShadow: "0px 10px 30px rgba(0,0,0,0.35)",
                        "&:hover": { background: "#22c55e" },
                        textTransform: "none",
                        fontWeight: 900,
                      }}
                      size="large"
                      href={active.cta.href}
                      endIcon={<ArrowForward />}
                    >
                      {active.cta.label}
                    </Button>
                  </Box>
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SolutionsPage;